import React, { useMemo } from 'react';
import { getModelTitle } from 'src/services/carList/utils/Car.utils';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import useTranslations from '@hooks/useTranslations';
import { TrimTitleSectionProps } from '@components/v2/TrimTitleSection/index';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Icons } from '@components/Icon';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { isBrandAC, ModalVersion } from 'src/constants/main';
import { Redux } from 'src/redux/redux.interface';
import FilterDuck from 'src/redux/filters/filter.duck';
import TrimSelectorDuck from 'src/redux/trimSelector/trimSelector.duck';
import { useGTM } from '@hooks/useGTM';
import { GtmData } from '../../../types/gtm';
import { Benefits } from '@components/Benefits';
import TitleWithBenefits from '@components/v2/TitleWithBenefits';
import FilterChip from '../../../ui/FilterChip';
import Link from '@components/Link';

export const TrimTitleSectionTemplate = ({ className, openFilter, onClickCTAFilters }: TrimTitleSectionProps) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const { carDetails } = useCarDetailsDuck();
    const { pushToDataLayer } = useGTM();
    const shouldIncludeBodyStyleDescription = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_SHOW_GR_BODY_STYLE_IN_CAR_TITLE
    );
    const isTrimComparatorEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_TRIM_COMPARATOR_ENABLED);

    const filters = useSelector((state: Redux) => FilterDuck.getFilters(state));
    const selectedFiltersCount = useMemo(() => filters.filter((filter) => !!filter.value).length, [filters]);

    const filteredTrims = useSelector((state: Redux) => TrimSelectorDuck.getFilteredTrims(state));

    const openTrimModal = () => {
        pushToDataLayer({
            event: 'uaevent',
            eventCategory: 'Content',
            eventAction: 'Display:Comparator',
            eventLabel: 'Finition',
        } as GtmData);
        dispatch(
            UIDuck.openModal({
                data: { data: {} },
                callbacks: {},
                modalType: UIDuck.MODAL_TYPES.TRIM_COMPARE,
                modalVersion: ModalVersion.v2,
                modalProperties: {
                    withoutPadding: true,
                    fullWidthOnDesktop: filteredTrims?.length >= 4,
                    middleWidthOnDesktop: filteredTrims?.length === 3,
                },
            })
        );
    };

    return (
        <div className={className}>
            <span className="title">{getModelTitle(carDetails, shouldIncludeBodyStyleDescription)}</span>
            <div className="trimBenefitsContainer">
                <TitleWithBenefits onlyBenefits />
            </div>
            <FilterChip
                label={t('filters.filters')}
                countOfSelectedFilters={selectedFiltersCount}
                dataId="TESTING_SELECTOR_FILTERS_OPEN_BUTTON"
                onClick={() => {
                    openFilter();
                    onClickCTAFilters(t('filters.filters'));
                }}
            />
            <span className="subTitle">{t('topNavbar.trimTitle')}</span>
            {isTrimComparatorEnabled && (filteredTrims?.length > 1 || selectedFiltersCount > 0) && (
                <Link
                    label={t('trim.comparator.button')}
                    onClick={openTrimModal}
                    dataTestId="TESTING_SELECTOR_TRIM_COMPARE_OPEN_BUTTON"
                    withArrowIcon
                    primary={!isBrandAC}
                    secondary={isBrandAC}
                />
            )}
        </div>
    );
};
